import type { Stripe } from '@stripe/stripe-js';

import { reportConsultError } from './report-consult-error.js';

/**
 * Stripe publishable key for `loadStripe` / Elements.
 *
 * @remarks Set `VITE_STRIPE_PUBLISHABLE_KEY` in `.env` locally and in the hosted build
 * environment. Public key by design — never a secret key. This is the NourishedRx account, NOT
 * Inbox Health's: the administrative-fee card must never be billed for healthcare expenses.
 */
function readStripePublishableKey(): string | undefined {
  return (import.meta as unknown as { env: Record<string, string> }).env[
    'VITE_STRIPE_PUBLISHABLE_KEY'
  ]?.trim();
}

/**
 * Builds the lazy Stripe.js accessor. The returned getter yields `null` when no publishable key is
 * configured, which `AdminFeeCardStep` renders as the support affordance rather than a blank tab.
 *
 * The import must stay dynamic. `@stripe/stripe-js` injects the js.stripe.com script during its OWN
 * module evaluation — `Promise.resolve().then(getStripePromise)` in `dist/index.mjs`, before any
 * `loadStripe` call — so a static import fetches from Stripe on every page load of this SPA,
 * including for the majority of members who never reach the card step.
 *
 * The key is a parameter rather than read here because vite inlines `import.meta.env` at transform
 * time and leaves it empty under vitest, so a getter reading it directly could never be exercised
 * by a test.
 */
export function createStripeGetter(
  publishableKey: string | undefined,
): () => Promise<Stripe | null> | null {
  const key = publishableKey?.trim();
  if (!key) return () => null;

  let pending: Promise<Stripe | null> | null = null;

  return () => {
    // Shared across concurrent callers and re-mounts, so one member session injects the script once.
    pending ??= import('@stripe/stripe-js')
      .then(({ loadStripe }) => loadStripe(key))
      .catch((err: unknown) => {
        // Cleared so a later call re-attempts rather than re-attaching to a settled rejection.
        // @stripe/stripe-js clears its own cache on error to make that work ("if previous attempts
        // are unsuccessful, will re-load script").
        pending = null;
        reportConsultError('stripe-load', err);
        throw err;
      });
    return pending;
  };
}

/**
 * Shared Stripe.js accessor — yields `null` when the publishable key isn't configured.
 *
 * Calling it is what loads Stripe.js; importing this module does nothing.
 */
export const getStripe = createStripeGetter(readStripePublishableKey());
